import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { confirmSufficientFunds, ledgerMoney } from 'components/TrustLedgerGrid'
import { useMemo, useState } from 'react'
import { Modal } from 'stories/components'
import { removeComma, solveDecimalJavascriptSum } from 'utils'

import { TRUST_LEDGER_SUB_TABS, TRUST_LEDGER_TABS } from './constants'

interface IDestination {
  loanNumber: string
  bucket: string
  amount: string
}

const blankDestination = (bucket: string): IDestination => ({ loanNumber: '', bucket, amount: '' })

/**
 * Move money off this loan's sub-ledger onto other loans' sub-ledgers.
 *
 * Several destinations at once because that is the shape of the request: Longhorn holds repair
 * holdback funds on one file and settles a handful of other files' interest out of them in one
 * sitting (Smartsheet row 431). The backend writes each destination as a matched pair - a payment
 * here and a deposit there, tied together - so no loan can be left holding half a transfer.
 */
export default function SubLedgerTransferModal({
  fromBucket,
  fromTitle,
  available,
  onSubmit,
  onClose,
}: {
  fromBucket: string
  fromTitle: string
  /** What this bucket holds right now, as the tab itself struck it. */
  available: number
  onSubmit: (data: Record<string, any>) => Promise<any>
  onClose: (changed: boolean) => void
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [reference, setReference] = useState('')
  const [notes, setNotes] = useState('')
  // Interest is where this money is going in every case we have been asked about, so it leads.
  const [destinations, setDestinations] = useState<IDestination[]>([blankDestination('interest')])

  const setDestination = (index: number, key: keyof IDestination, value: string) =>
    setDestinations((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)))

  const total = useMemo(
    () => Number(solveDecimalJavascriptSum(destinations.map((row) => removeComma(row.amount))).toFixed(2)),
    [destinations],
  )

  /** What stays behind. Shown rather than only checked: it is what stops a mistyped amount. */
  const remaining = Number(solveDecimalJavascriptSum([removeComma(available), -1 * total]).toFixed(2))

  const submit = async () => {
    setError('')
    const rows = destinations.filter((row) => String(row.loanNumber).trim() || removeComma(row.amount))
    if (!rows.length) return setError('Add at least one loan to transfer to')
    for (const row of rows) {
      if (!(Number(String(row.loanNumber).trim()) > 0)) return setError('Enter a loan number for every transfer')
      if (!(removeComma(row.amount) > 0)) return setError(`Enter an amount for loan ${row.loanNumber}`)
    }
    if (!String(reference).trim()) return setError('Enter a reference')

    // Only the source bucket can be overdrawn by this - every destination is receiving money -
    // and it asks rather than refuses, the same way every other ledger entry on this screen does.
    if (!(await confirmSufficientFunds(total, removeComma(available), fromTitle))) return

    setLoading(true)
    try {
      await onSubmit({
        fromBucket,
        date,
        reference: String(reference).trim(),
        notes,
        destinations: rows.map((row) => ({
          loanNumber: Number(String(row.loanNumber).trim()),
          bucket: row.bucket,
          amount: removeComma(row.amount),
        })),
      })
      onClose(true)
    } catch (err: any) {
      setError(err?.message || 'Could not record the transfer')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={`Transfer from ${fromTitle}`}
      titleOkay="Transfer"
      loading={loading}
      onClose={() => onClose(false)}
      onOk={submit}
      isOpen
    >
      <div className="text-gray-900 w-[46rem] mt-2 mb-3">
        <div className="text-[13px] text-gray-500 mb-3">
          {/* Worded like the overdraw warning rather than shown as a minus figure, which reads as a typo. */}
          {removeComma(available) < 0
            ? `${fromTitle} is overdrawn by ${ledgerMoney(-1 * removeComma(available))}`
            : `${fromTitle} holds ${ledgerMoney(available)}`}
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <div className="text-[13px] text-gray-500 mb-1">Date</div>
            <input
              type="date"
              className="text-[14px] border rounded px-2 py-2 w-full"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div>
            <div className="text-[13px] text-gray-500 mb-1">Reference</div>
            <input
              className="text-[14px] border rounded px-2 py-2 w-full"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </div>
          <div>
            <div className="text-[13px] text-gray-500 mb-1">Notes</div>
            <input
              className="text-[14px] border rounded px-2 py-2 w-full"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <div className="text-[13px] text-gray-500 mb-1">Transfer to</div>
        <table className="w-full text-[14px]">
          <thead className="bg-gray-50 text-[13px] text-gray-500">
            <tr>
              <th className="text-left font-normal px-2 py-2">Loan Number</th>
              <th className="text-left font-normal px-2 py-2">Ledger</th>
              <th className="text-left font-normal px-2 py-2">Amount</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {destinations.map((row, index) => (
              <tr key={index} className="border-b">
                <td className="px-2 py-2">
                  <input
                    className="text-[14px] border rounded px-2 py-2 w-full"
                    value={row.loanNumber}
                    onChange={(e) => setDestination(index, 'loanNumber', e.target.value)}
                  />
                </td>
                <td className="px-2 py-2">
                  <select
                    className="text-[14px] border rounded px-2 py-2 w-full"
                    value={row.bucket}
                    onChange={(e) => setDestination(index, 'bucket', e.target.value)}
                  >
                    {TRUST_LEDGER_SUB_TABS.map((bucket) => (
                      <option key={bucket} value={bucket}>
                        {TRUST_LEDGER_TABS[bucket]}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-2">
                  <input
                    className="text-[14px] border rounded px-2 py-2 w-full"
                    value={row.amount}
                    onChange={(e) => setDestination(index, 'amount', e.target.value)}
                  />
                </td>
                <td className="px-2 py-2 text-center">
                  {destinations.length > 1 && (
                    <button
                      className="text-gray-400 hover:text-red-600"
                      onClick={() => setDestinations((prev) => prev.filter((_, i) => i !== index))}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <button
          className="flex items-center text-[14px] text-shade-blue hover:underline mt-3"
          onClick={() => setDestinations((prev) => [...prev, blankDestination('interest')])}
        >
          <PlusIcon className="w-4 h-4 mr-1" /> Add another loan
        </button>

        <div className="flex justify-end gap-6 text-[14px] mt-4">
          <div>
            <span className="text-gray-500 mr-2">Total transferred</span>
            <span className="font-variation-settings-600">{ledgerMoney(total)}</span>
          </div>
          <div>
            <span className="text-gray-500 mr-2">Left in {fromTitle}</span>
            <span className={`font-variation-settings-600 ${remaining < 0 ? 'text-red-600' : ''}`}>
              {ledgerMoney(remaining)}
            </span>
          </div>
        </div>

        {!!error && <div className="text-[13px] text-red-600 mt-3">{error}</div>}
      </div>
    </Modal>
  )
}
